import React, { useState } from 'react';
import { Loader2, Scale } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { staffAttApi, errMsg, fmtTime } from '@/services/staffAttendanceApi';

const CHOICES = [['full', 'Full'], ['half', 'Half'], ['none', 'None']];
const roleLabel = (r) => ({ truck_loadman: 'Truck Loadman', unloading_loadman: 'Unloading Loadman' }[r] || 'Regular');

const Decide = ({ r, canDecide, busy, onPick }) => {
  if (r.fix_checkout) {
    return <span className="text-xs text-amber-700" data-testid={`review-decision-${r.key}`}>Fix check-out</span>;
  }
  if (!canDecide) {
    return <span className={r.decision ? 'font-semibold' : 'text-amber-700'} data-testid={`review-decision-${r.key}`}>{r.decision ? r.decision.toUpperCase() : 'Pending'}</span>;
  }
  return (
    <div className="flex gap-1" data-testid={`review-decision-${r.key}`}>
      {CHOICES.map(([v, l]) => (
        <button key={v} type="button" disabled={busy} onClick={() => onPick(r, v)}
          className={`rounded-md border px-2.5 py-1 text-xs font-semibold transition-colors ${r.decision === v ? 'bg-[#2D5F4F] text-white border-[#2D5F4F]' : 'bg-white text-gray-700 border-gray-300 hover:border-[#2D5F4F]'}`}
          data-testid={`review-${v}-${r.key}`}>{l}</button>
      ))}
      {busy && <Loader2 className="w-4 h-4 animate-spin text-amber-600 self-center" />}
    </div>
  );
};

/** Flagged staff-days (Day 5–8 / over 11 hrs, Night outside 6 pm–2 am, missed check-out) — not paid until HR picks Full / Half / None. */
export const PayrollReviewList = ({ reviews, canDecide, onChanged, decide = staffAttApi.setPayDecision, groupLabel = 'Agency' }) => {
  const [busy, setBusy] = useState('');
  if (!reviews?.length) return null;
  const open = reviews.filter((r) => !r.decision && !r.fix_checkout).length;
  const pick = async (r, v) => {
    setBusy(r.key);
    try {
      await decide({ staff_id: r.staff_id, date: r.date, shift: r.shift, work_role: r.work_role, decision: r.decision === v ? null : v });
      onChanged();
    } catch (e) { toast.error(errMsg(e, 'Could not save the decision')); } finally { setBusy(''); }
  };
  return (
    <Card className="border-0 shadow-md overflow-hidden" data-testid="payroll-review">
      <div className="flex flex-wrap items-center gap-3 px-4 py-3 bg-amber-50 border-b border-amber-200">
        <Scale className="w-5 h-5 text-amber-700" />
        <div className="font-bold text-amber-900">HR to decide</div>
        <span className="rounded-full bg-amber-600 text-white text-xs px-2 py-0.5" data-testid="payroll-review-open">{open} pending</span>
        <span className="text-xs text-amber-800">of {reviews.length} flagged staff-day(s) — not paid until HR picks Full / Half / None{canDecide ? ' (tap again to clear)' : ''}</span>
      </div>
      <CardContent className="p-0 overflow-x-auto max-h-[420px] overflow-y-auto">
        <Table>
          <TableHeader><TableRow>{['Date', 'Staff', groupLabel, 'Shift', 'Work type', 'Hours', 'In → Out', 'Reason', 'Decision'].map((h) => <TableHead key={h}>{h}</TableHead>)}</TableRow></TableHeader>
          <TableBody>
            {reviews.map((r) => (
              <TableRow key={r.key} className={r.decision ? '' : 'bg-amber-50/40'} data-testid={`review-row-${r.key}`}>
                <TableCell className="font-mono text-xs">{r.date}</TableCell>
                <TableCell><div className="font-medium">{r.staff_name}</div><div className="text-xs text-gray-500 font-mono">{r.staff_id}</div></TableCell>
                <TableCell className="text-xs">{r.agency}</TableCell>
                <TableCell>{r.shift}</TableCell>
                <TableCell className="text-xs">{roleLabel(r.work_role)}</TableCell>
                <TableCell className="tabular-nums">{r.hours}</TableCell>
                <TableCell className="text-xs font-mono">{fmtTime(r.first_in)} → {r.last_out ? fmtTime(r.last_out) : '—'}</TableCell>
                <TableCell className="text-xs text-gray-700">{r.reason}{r.decided_by_name && <div className="text-gray-400">by {r.decided_by_name}</div>}</TableCell>
                <TableCell><Decide r={r} canDecide={canDecide} busy={busy === r.key} onPick={pick} /></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
};

export default PayrollReviewList;
